import { ITheme } from './theme.interface';
import { ThemeScale } from './theme-scale.type';

/**
 * Turns a theme into the stylesheet that defines its CSS variables (IC-840):
 * mode-independent tokens and light colours on `:root`, dark colours under
 * `[data-theme="dark"]` / `.dark` (the switch ThemeService already flips), and
 * each section's overrides scoped to that section.
 */
export class ThemeCss {
  private static readonly _darkScopes = [':root[data-theme="dark"]', '.dark'];

  /**
   * Sections that existed before the theme system keep their class: the service
   * calendar marks its parts with `.ib-service` (IC-830). New code uses
   * `data-section="<name>"`.
   */
  private static readonly _legacySectionClasses: Record<string, string> = {
    service: '.ib-service',
  };

  /** `heightSm` → `height-sm`, `0.5` → `0_5`: a valid custom property name. */
  static tokenName(key: string): string {
    return key
      .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
      .replace(/\./g, '_')
      .toLowerCase();
  }

  /** The CSS variable a theme value ends up in, e.g. `ThemeCss.variable('color', 'surface')`. */
  static variable(group: string, key: string): string {
    return `--t-${group}-${ThemeCss.tokenName(key)}`;
  }

  static toCss(theme: ITheme): string {
    const root = [
      ...ThemeCss._declarations('font', theme.font.family),
      ...ThemeCss._declarations('text', theme.font.size),
      ...ThemeCss._declarations('weight', theme.font.weight),
      ...ThemeCss._declarations('leading', theme.font.leading),
      ...ThemeCss._declarations('space', theme.space),
      ...ThemeCss._declarations('radius', theme.radius),
      ...ThemeCss._declarations('border', theme.border),
      ...ThemeCss._declarations('control', theme.control),
      ...ThemeCss._declarations('color', theme.colors.light),
      ...ThemeCss._declarations('shadow', theme.shadow.light),
    ];
    const dark = [...ThemeCss._declarations('color', theme.colors.dark), ...ThemeCss._declarations('shadow', theme.shadow.dark)];

    const rules = [ThemeCss._rule([':root'], root), ThemeCss._rule(ThemeCss._darkScopes, dark)];

    for (const [name, section] of Object.entries(theme.sections)) {
      const selectors = ThemeCss._sectionSelectors(name);
      rules.push(ThemeCss._rule(selectors, ThemeCss._declarations('color', section.light ?? {})));
      const darkSelectors = ThemeCss._darkScopes
        .flatMap((scope) => selectors.map((s) => `${scope} ${s}`))
        .concat(selectors.map((s) => `.dark${s}`));
      rules.push(ThemeCss._rule(darkSelectors, ThemeCss._declarations('color', section.dark ?? {})));
    }

    return `/* tema: ${theme.id} */\n${rules.filter(Boolean).join('\n\n')}\n`;
  }

  private static _declarations(group: string, scale: ThemeScale): string[] {
    return Object.entries(scale).map(([key, value]) => `${ThemeCss.variable(group, key)}: ${value};`);
  }

  private static _rule(selectors: string[], body: string[]): string {
    if (!body.length) return '';
    return `${selectors.join(',\n')} {\n  ${body.join('\n  ')}\n}`;
  }

  private static _sectionSelectors(name: string): string[] {
    const own = [`[data-section="${name}"]`];
    const legacy = ThemeCss._legacySectionClasses[name];
    return legacy ? [...own, legacy] : own;
  }
}
